import type { IconName } from '@evogard/ui';
import {
  fromColumn,
  format,
  houseLocalOf,
  type AssistanceServiceType,
  type DispatchStatus,
  type ProviderPaymentMode,
  type Tone,
} from '@evogard/domain';

/** A raiz das consultas do módulo — invalidar ela relê lista, agenda e acertos. */
export const ASSISTANCE_KEY = ['assistencia'] as const;
export const DISPATCHES_KEY = [...ASSISTANCE_KEY, 'acionamentos'] as const;
export const AGENDA_KEY = [...ASSISTANCE_KEY, 'agenda'] as const;
export const DRAFTS_KEY = [...ASSISTANCE_KEY, 'rascunhos'] as const;
export const SETTLEMENTS_KEY = [...ASSISTANCE_KEY, 'acertos'] as const;
export const MANAGEMENT_KEY = [...ASSISTANCE_KEY, 'gestao'] as const;

/** O ícone de cada serviço, no ladrilho e na lista. */
export const SERVICE_ICON: Record<AssistanceServiceType, IconName> = {
  reboque: 'assistencia',
  bateria: 'bateria',
  pneu: 'pneu',
  pane_seca: 'combustivel',
  chaveiro: 'chaveiro',
  residencial: 'casa',
  taxi: 'taxi',
  hospedagem: 'cama',
};

// @valor
export const PAYMENT_LABEL: Record<ProviderPaymentMode, string> = {
  a_vista: 'À vista · Assistência 24h',
  mensal: 'Mensal · Financeiro',
};

/** Metros → "45,1 km". */
export function km(m: number | null | undefined): string {
  if (m === null || m === undefined) return '—';
  return `${(m / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

/** Segundos → "24 min". */
export function minutes(s: number | null | undefined): string {
  if (s === null || s === undefined) return '—';
  return `${Math.max(1, Math.round(s / 60))} min`;
}

/** "197.27" → "R$ 197,27". */
export function reais(column: string | null | undefined): string {
  if (column === null || column === undefined) return '—';
  const value = fromColumn(column);
  return value === null ? '—' : format(value);
}

/** "21/10, 14:00" no relógio da casa. */
export function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const local = houseLocalOf(iso);
  return `${local.slice(8, 10)}/${local.slice(5, 7)}, ${local.slice(11, 16)}`;
}

/** O status do acionamento (P15) como a tela o mostra. */
export function situationOf(d: { status: DispatchStatus }): {
  label: string;
  tone: Tone;
} {
  switch (d.status) {
    case 'rascunho':
      return { label: 'Rascunho', tone: 'neutro' };
    case 'agendado':
      return { label: 'Agendado', tone: 'progresso' };
    case 'acionado':
      return { label: 'Acionado', tone: 'sucesso' };
    case 'cancelado':
      return { label: 'Cancelado', tone: 'neutro' };
  }
}

/** O traçado codificado do Google → pontos [lat, lng]. */
export function decodePolyline(text: string): [number, number][] {
  let i = 0;
  let lat = 0;
  let lng = 0;
  const points: [number, number][] = [];
  while (i < text.length) {
    let shift = 0;
    let result = 0;
    let byte: number;
    do {
      byte = text.charCodeAt(i++) - 63;
      result |= (byte & 31) << shift;
      shift += 5;
    } while (byte >= 32 && i < text.length);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0;
    result = 0;
    do {
      byte = text.charCodeAt(i++) - 63;
      result |= (byte & 31) << shift;
      shift += 5;
    } while (byte >= 32 && i < text.length);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
}
